import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/hooks/useRedux';
import { setCurrentPlan } from '@/redux/planSlice';
import PlaneIcon from '@/assets/icons/plane.svg?react';
import HotelIcon from '@/assets/icons/hotel.svg?react';
import usePriceCurrency from '@/hooks/usePriceCurrency';
import busIcon from '@/assets/icons/bus-green.png';
import getSelectedPlan from '@/utils/getSelectedPlan';
import useRoutes from '@/hooks/useRoutes';
import usePlan from '@/hooks/usePlan';
import styles from './DestinationCard.module.scss';

interface ICardImage {
    jpg: string;
    webp?: string;
}

interface ICard {
    name: string;
    summary: string;
    cardImage: ICardImage;
    item: number;
    isClickable?: boolean;
    mode?: 'carousel' | 'gift';
}

const DestinationCard = ({ name, summary, cardImage, item: planId, isClickable = true, mode }: ICard) => {
    const { routes, currentPathname, experiencesInfoRoutes } = useRoutes();
    const isGiftPlanSelectionPage = currentPathname === routes.giftExperienceType;
    const { planConfig, getPlanFromPrice } = usePlan(planId);
    const planFromPrice = getPlanFromPrice({ showMinPrice: true });
    const isForCarousel = mode === 'carousel';
    const isForGiftTravel = mode === 'gift';
    const { isGetawayPlan, isVipPlan } = getSelectedPlan(planId);
    const { PriceCurrency } = usePriceCurrency();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleClick = () => {
        dispatch(setCurrentPlan(planId));

        if (isGiftPlanSelectionPage) {
            switch (planId) {
                case 1:
                    navigate(routes.giftExperienceOptionsDestination);
                    break;
                case 3:
                    navigate(routes.giftExperienceOptionsGetaway);
                    break;
                case 4:
                    navigate(routes.giftExperienceOptionsSummer);
                    break;
                case 7:
                    navigate(routes.giftExperienceOptionsNational);
                    break;
                default:
                    break;
            }
        } else {
            const destinationUrl = experiencesInfoRoutes[planId];
            navigate(destinationUrl, { state: { from: 'destinationCard' } });
        }
    };

    const getCardStyle = () => {
        if (isForCarousel) return `${styles.card} ${styles.cardContainerCarousel}`;
        if (isForGiftTravel) return `${styles.card} ${styles.cardContainerGift}`;
        if (!isClickable) return `${styles.card} ${styles.cardNotClickable}`;
        return styles.card;
    };

    const detail1Text = isGetawayPlan ? t('national') : t('roundTripFlights');
    const detail2Text = isVipPlan ? `${t('hotel')} 4/5*` : `${t('hotel')}`;
    const planGradient = `linear-gradient(220deg, ${planConfig.colors.dark}, ${planConfig.colors.light})`;

    return (
        <button className={getCardStyle()} onClick={handleClick}>
            <div className={styles.cardHeader}>
                <picture className={styles.cardImage}>
                    <source srcSet={cardImage.webp} type="image/webp" />
                    <source srcSet={cardImage.jpg} type="image/jpeg" />
                    <img src={cardImage.jpg} alt={t(`${name}`)} />
                </picture>
                <div className={styles.cardTitle} style={{ background: planGradient }}>
                    <span>{t(`${name}`)}</span>
                </div>
            </div>
            <div className={styles.cardBody}>
                <p className={styles.cardDescription}>{t(`${summary}`)}</p>
                <div className={`${styles.cardDetails} ${planConfig.slug}`}>
                    <div className={styles.cardDetail}>
                        {isGetawayPlan ? (
                            <img src={busIcon} className={`${styles.icon1} ${styles.iconGetaway}`} alt="" />
                        ) : (
                            <PlaneIcon className={styles.icon1} />
                        )}
                        <p>{detail1Text}</p>
                    </div>
                    <div className={styles.cardDetail}>
                        <HotelIcon className={styles.icon2} />
                        <p>{detail2Text}</p>
                    </div>
                </div>
                <div className={styles.buttonAndPrice}>
                    <span className={styles.infoButton} style={{ backgroundImage: planGradient }}>
                        {isForGiftTravel ? t('buy') : t('info')}
                    </span>
                    <div className={styles.cardPrice}>
                        {t('from')}{' '}
                        <span style={{ backgroundImage: planGradient }}>
                            <PriceCurrency price={planFromPrice} />
                        </span>
                    </div>
                </div>
            </div>
        </button>
    );
};

export default DestinationCard;
